import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type ImproveMissingField = { key: string; label: string; hint: string }

export type ImproveCompleteness = {
  ok: boolean
  score: number
  missing: ImproveMissingField[]
}

export type ImproveChange = {
  id: string
  kind: 'field' | 'skill' | 'skill_type' | 'remove_skill' | 'experience' | 'role_update' | 'role_skill' | 'new_role'
  label: string
  field?: string
  requirement_type?: 'required' | 'preferred'
  before: unknown
  after: unknown
}

export type ImproveChip = {
  id: string
  skill_id: string
  name: string
  /** Profile chips: roles whose description mentions the skill (empty = profile-level skill). */
  role_ids?: string[]
  target?: string
  requirement_type?: 'required' | 'preferred'
}

export type ImprovePreview = {
  token: string
  repair_available: boolean
  before: ImproveCompleteness
  after: ImproveCompleteness
  changes: ImproveChange[]
  chips: ImproveChip[]
}

export type ImproveApplyResult = {
  applied: number
  stale_evaluations: number
  completeness: ImproveCompleteness
}

export type ImproveTarget = { kind: 'profile' } | { kind: 'opportunity'; opportunityId: string }

export type ImproveApplyInput = {
  token: string
  accepted: string[]
  chips: { id: string; requirement_type?: 'required' | 'preferred' }[]
}

function basePath(target: ImproveTarget): string {
  return target.kind === 'profile'
    ? `${API_ORIGIN}/api/improve/profile`
    : `${API_ORIGIN}/api/improve/opportunities/${target.opportunityId}`
}

export function improveQueryKey(target: ImproveTarget) {
  return target.kind === 'profile'
    ? ['improve', 'profile']
    : ['improve', 'opportunity', target.opportunityId]
}

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message = (body as { detail?: string; error?: string }).detail ?? (body as { error?: string }).error
    throw new Error(message || 'Something went wrong. Please try again.')
  }
  return body as T
}

/** ``version`` (e.g. the record's ``updated_at``) refetches after edits made elsewhere on the page. */
export function useImproveCompleteness(target: ImproveTarget, version?: string | null) {
  return useQuery({
    queryKey: [...improveQueryKey(target), version ?? null],
    queryFn: async () => readJson<ImproveCompleteness>(await authorizedFetch(`${basePath(target)}/`)),
    staleTime: 30_000,
  })
}

export function useImprovePreview(target: ImproveTarget) {
  return useMutation({
    mutationFn: async () =>
      readJson<ImprovePreview>(await authorizedFetch(`${basePath(target)}/preview/`, { method: 'POST' })),
  })
}

export function useImproveApply(target: ImproveTarget) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ImproveApplyInput) => {
      const chips = target.kind === 'profile' ? input.chips.map((chip) => chip.id) : input.chips
      const response = await authorizedFetch(`${basePath(target)}/apply/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: input.token, accepted: input.accepted, chips }),
      })
      return readJson<ImproveApplyResult>(response)
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: improveQueryKey(target) })
      if (target.kind === 'profile') {
        void queryClient.invalidateQueries({ queryKey: ['profile'] })
      } else {
        void queryClient.invalidateQueries({ queryKey: ['opportunity', target.opportunityId] })
        void queryClient.invalidateQueries({ queryKey: ['organisation-opportunity'] })
        void queryClient.invalidateQueries({ queryKey: ['organisation-opportunities'] })
      }
    },
  })
}
